import { useTheme } from '@/contexts/ThemeContext';
import { brandSymbol } from '@/assets/branding';

// ─── Size map ────────────────────────────────────────────────────────────────
const SIZE_MAP = {
  sm: { img: 'h-9 w-9',   halo: '240%' },
  md: { img: 'h-14 w-14', halo: '230%' },
  lg: { img: 'h-20 w-20', halo: '220%' },
};

// ─── Sheep icon with breathing + ambient halo ────────────────────────────────
function SheepIcon({ size = 'md', isDark }) {
  const { img, halo } = SIZE_MAP[size] ?? SIZE_MAP.md;

  return (
    <div className="relative flex items-center justify-center">
      {/* Ambient halo — pulsing glow ring behind the symbol */}
      <div
        className="absolute rounded-full animate-logo-halo pointer-events-none"
        style={{
          width: halo,
          height: halo,
          background: isDark
            ? 'radial-gradient(circle, rgba(99,152,255,0.22) 0%, transparent 65%)'
            : 'radial-gradient(circle, rgba(27,79,156,0.22) 0%, transparent 65%)',
        }}
      />

      {/* Brand symbol — breathing scale + drop-shadow glow */}
      <img
        src={brandSymbol}
        alt=""
        aria-hidden="true"
        draggable={false}
        className={`relative ${img} animate-breathe object-contain select-none`}
        style={{
          filter: isDark
            ? 'brightness(0) invert(1) drop-shadow(0 0 9px rgba(99,152,255,0.5))'
            : 'drop-shadow(0 0 8px rgba(27,79,156,0.32))',
        }}
      />
    </div>
  );
}

// ─── BrandLoader ─────────────────────────────────────────────────────────────
/**
 * Versatile mid-session loading indicator using the Kéves brand symbol.
 *
 * Modes:
 *   overlay   — fixed inset-0 z-[60], translucent bg + backdrop-blur
 *   inline    — no positioning, centers content inside its parent
 *   (default) — full-page bg-page-bg replacement (for route guards / Suspense)
 *
 * Props:
 *   overlay     {boolean}  Show as floating overlay over existing UI
 *   inline      {boolean}  Embed inline (no positioning)
 *   transparent {boolean}  Skip background entirely
 *   blur        {boolean}  Enable backdrop-blur in overlay mode (default true)
 *   label       {string}   Optional subtle label below the icon
 *   size        {"sm"|"md"|"lg"}  Icon size (default "md")
 */
export default function BrandLoader({
  overlay     = false,
  inline      = false,
  transparent = false,
  blur        = true,
  label,
  size        = 'md',
}) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const inner = (
    <div className="flex flex-col items-center gap-4">
      <SheepIcon size={size} isDark={isDark} />
      {label && (
        <p className="text-[11px] font-medium tracking-wide text-slate-400 dark:text-slate-500">
          {label}
        </p>
      )}
    </div>
  );

  // ── Inline ────────────────────────────────────────────────────────────────
  if (inline) {
    return (
      <div className="flex items-center justify-center py-10">
        {inner}
      </div>
    );
  }

  // ── Overlay ───────────────────────────────────────────────────────────────
  if (overlay) {
    return (
      <div
        className={`fixed inset-0 z-[60] flex items-center justify-center ${
          blur ? 'backdrop-blur-[2px]' : ''
        } ${
          transparent
            ? 'bg-transparent'
            : isDark
            ? 'bg-black/25'
            : 'bg-black/15'
        }`}
      >
        {inner}
      </div>
    );
  }

  // ── Full-page (default) ───────────────────────────────────────────────────
  return (
    <div className="min-h-[100dvh] bg-page-bg flex items-center justify-center">
      {inner}
    </div>
  );
}
